
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useSupabase } from "@/hooks/useSupabase";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import SignupForm from "@/components/auth/SignupForm";
import EmailVerificationScreen from "@/components/auth/EmailVerificationScreen";

const Signup = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isAgree, setIsAgree] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const { signUp, isLoggedIn } = useSupabase();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we have a return URL
  const returnTo = location.state?.returnTo || '/';

  useEffect(() => {
    if (isLoggedIn) {
      navigate(returnTo);
    }
  }, [isLoggedIn, navigate, returnTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAgree) {
      toast({
        title: "Please agree to the terms",
        description: "You must agree to the terms and privacy policy to create an account.",
        variant: "destructive",
      });
      return;
    }
    
    console.log(`[${new Date().toISOString()}] Form submitted, starting signup process...`);
    setIsLoading(true);
    try {
      const success = await signUp(email, password, name);
      
      if (success) {
        setIsEmailSent(true);
        toast({
          title: "Verification email sent",
          description: "Please check your email to verify your account.",
        });
      }
    } catch (err: any) {
      console.error("Signup unexpected error:", err);
      toast({
        title: "Sign up failed",
        description: err?.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      console.log("Signup page: Starting Google sign in...");
      console.log("Current URL origin:", window.location.origin);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) {
        console.error("Google sign in error from signup page:", error);
        toast({
          title: "Sign up failed",
          description: error.message || "Failed to connect with Google",
          variant: "destructive",
        });
        setIsLoading(false);
      } else {
        console.log("Google sign in initiated successfully from signup page:", data);
        // The redirect will happen automatically
      }
    } catch (err: any) {
      console.error("Unexpected error during Google sign in from signup page:", err);
      toast({
        title: "Sign up failed",
        description: err?.message || "An unexpected error occurred",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  if (isLoggedIn) {
    return <div className="min-h-[calc(100vh-16rem)] flex items-center justify-center">Redirecting...</div>;
  }

  return (
    <Layout>
      <div className="min-h-[calc(100vh-16rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        {isEmailSent ? (
          <EmailVerificationScreen 
            email={email}
            onRetry={() => {
              setIsEmailSent(false);
              setIsLoading(false);
            }}
          />
        ) : (
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center">Create an account</CardTitle>
              <CardDescription className="text-center">
                Sign up to access all features
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SignupForm
                onSubmit={handleSubmit}
                onGoogleSignIn={handleGoogleSignIn}
                name={name}
                setName={setName}
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                isAgree={isAgree}
                setIsAgree={setIsAgree}
                isLoading={isLoading}
              />
            </CardContent>
            <CardFooter className="flex flex-col space-y-2">
              <div className="text-center text-sm">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-ai-indigo hover:text-ai-purple">
                  Sign in
                </Link>
              </div>
            </CardFooter>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Signup;
